// Window_ModUI.tsx — Buildings Set1 §8 editor for `Mod_Window`. Surfaces the window's inheritable
// fields (width/height/sillHeight/pane divisions) via `InheritableField`, with resolved
// values + provenance from the §3 fallback chain. P2 adds the "bound to wall segment" readout.
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Text} from "react-vcomponents";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_Window} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Window.js";
import {ResolveBuildingDefaults, ResolveWindowParams, FindOwningRoom, FindOwningBuilding} from "../../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {ResolveOpeningBinding} from "../../../../../Server/DB/mapObjects/BuildingGeometryAccessors.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {InheritableField} from "./Building/InheritableField.js";
import {BindingReadout} from "./Door_ModUI.js";

export const Window_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper, obj} = props;
	const Change = (mutator: (data: Mod_Window)=>any)=>ChangeModWrapper(a=>mutator(a.window!));
	const data = mod.window!;

	const resolved = ResolveWindowParams(obj);
	const resolvedDefaults = ResolveBuildingDefaults(obj);
	const prov = (path: string)=>resolvedDefaults.provenance[path];

	const inheritField = (key: keyof Mod_Window, label: string, number: {min?: number; max?: number; step?: number})=>{
		const resolvedValue = resolved[key] as number;
		const provenance = prov(`window.${key}`);
		return <InheritableField key={key} label={label} info="Inherited via the building fallback chain (Room → Level → Building → prototypes → hardcoded)."
			resolvedValue={resolvedValue} value={data[key] ?? null}
			provenance={provenance}
			onChange={(v)=>Change(a=>{ (a as any)[key] = v as number|n; })}
			number={number}/>;
	};

	// P2 — the "bound to wall segment" readout (or a red "no wall within tolerance" warning).
	// Set2 F3: bound to the FLOOR's merged wall segments.
	const roomOwner = FindOwningRoom(obj);
	const buildingOwner = FindOwningBuilding(obj);
	const bindingRes = ResolveOpeningBinding(obj);
	const binding = bindingRes?.binding ?? null;

	return (
		<>
			<Text style={{color: "#999", fontSize: 12}}>Window opening in the closest ancestor Room's wall. Geometry lands in P4.</Text>
			<BindingReadout roomOwner={roomOwner?.obj} buildingOwner={buildingOwner?.obj} binding={binding} segment={bindingRes?.segment}/>
			{inheritField("width", "Width (m)", {min: 0.2, max: 6, step: 0.1})}
			{inheritField("height", "Height (m)", {min: 0.2, max: 6, step: 0.1})}
			{inheritField("sillHeight", "Sill height (m)", {min: 0, max: 6, step: 0.1})}
			{inheritField("paneDivisionsX", "Pane divisions (X)", {min: 1, max: 8, step: 1})}
			{inheritField("paneDivisionsY", "Pane divisions (Y)", {min: 1, max: 8, step: 1})}
		</>
	);
});
